import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Save, ShoppingCart, TrendingUp, Download, Filter } from 'lucide-react';

const PRODUCTS = ["Fonasa", "Zona", "1727", "Previsional", "Seg. Viaje", "Medis Group", "RXD + 18"];
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function VentasHistoricas() {
  const [tab, setTab] = useState('carga');
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [totales, setTotales] = useState({});
  const [saving, setSaving] = useState(false);
  const [evolution, setEvolution] = useState([]);
  const [evoYear, setEvoYear] = useState(new Date().getFullYear());
  const [loadingEvo, setLoadingEvo] = useState(false);
  const [productFilter, setProductFilter] = useState('Total');

  const mes = currentDate.getMonth() + 1;
  const año = currentDate.getFullYear();

  const fetchVendors = useCallback(async () => {
    try { const data = await api.getVendors(); setVendors(data.filter(v => v.status === 'active')); } catch (e) { /* handled */ }
  }, []);

  const fetchHistorical = useCallback(async (vendorId) => {
    try {
      const data = await api.getVentasHistoricas(vendorId, mes, año);
      setTotales(data.totales || {});
    } catch (e) { setTotales({}); }
  }, [mes, año]);

  const fetchEvolution = useCallback(async () => {
    setLoadingEvo(true);
    try {
      const data = await api.getEvolucionVendedores(evoYear);
      setEvolution(data);
    } catch (e) { toast.error('Error al cargar evolucion'); }
    finally { setLoadingEvo(false); }
  }, [evoYear]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { if (selectedVendor) fetchHistorical(selectedVendor.id); else setTotales({}); }, [selectedVendor, fetchHistorical]);
  useEffect(() => { if (tab === 'evolucion') fetchEvolution(); }, [tab, fetchEvolution]);

  const updateProduct = (product, value) => {
    setTotales(prev => ({ ...prev, [product]: parseInt(value) || 0 }));
  };

  const handleSave = async () => {
    if (!selectedVendor) return;
    setSaving(true);
    try {
      await api.updateVentasHistoricas({ vendedor_id: selectedVendor.id, mes, año, totales });
      toast.success(`Ventas de ${selectedVendor.nombre} guardadas`);
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const prevMonth = () => { setCurrentDate(new Date(año, mes - 2, 1)); setTotales({}); };
  const nextMonth = () => { setCurrentDate(new Date(año, mes, 1)); setTotales({}); };
  const monthLabel = currentDate.toLocaleDateString('es-UY', { month: 'long', year: 'numeric' });
  const totalGeneral = Object.values(totales).reduce((s, v) => s + (v || 0), 0);

  const getMonthValue = (vendorData, monthNum) => {
    const mesData = vendorData.meses[String(monthNum)];
    if (!mesData) return 0;
    if (productFilter === 'Total') return mesData.total || 0;
    return mesData.totales?.[productFilter] || 0;
  };

  const getVendorYearTotal = (vendorData) => {
    return Array.from({ length: 12 }, (_, i) => getMonthValue(vendorData, i + 1)).reduce((s, v) => s + v, 0);
  };

  const exportEvolutionExcel = async () => {
    const XLSX = await import('xlsx');
    const rows = [];
    evolution.forEach(v => {
      const row = { Vendedor: v.nombre, Ficha: v.ficha_casmu };
      if (productFilter === 'Total') {
        PRODUCTS.forEach(p => {
          let pTotal = 0;
          for (let m = 1; m <= 12; m++) pTotal += v.meses[String(m)]?.totales?.[p] || 0;
          row[p] = pTotal;
        });
      }
      let totalAnual = 0;
      for (let m = 1; m <= 12; m++) {
        const val = getMonthValue(v, m);
        row[MESES[m - 1]] = val;
        totalAnual += val;
      }
      row['Total'] = totalAnual;
      rows.push(row);
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Evolucion ${evoYear}`);
    XLSX.writeFile(wb, `Evolucion_Vendedores_${evoYear}.xlsx`);
  };

  return (
    <div className="space-y-6" data-testid="ventas-diarias">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Ventas Diarias</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Cargar ventas por producto y ver evolucion de vendedores</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="ventas-diarias-tabs">
        <button onClick={() => setTab('carga')}
          className={`px-4 py-2 text-[13px] font-medium rounded-md transition-all ${tab === 'carga' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-carga">
          <ShoppingCart size={14} className="inline mr-1.5 -mt-0.5" />Carga de Ventas
        </button>
        <button onClick={() => setTab('evolucion')}
          className={`px-4 py-2 text-[13px] font-medium rounded-md transition-all ${tab === 'evolucion' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-evolucion">
          <TrendingUp size={14} className="inline mr-1.5 -mt-0.5" />Evolucion Mensual
        </button>
      </div>

      {tab === 'carga' && (
        <>
          <div className="flex items-center gap-4 flex-wrap">
            <select value={selectedVendor?.id || ''} onChange={(e) => {
              const v = vendors.find(v => v.id === e.target.value);
              setSelectedVendor(v || null);
            }} className="form-input max-w-xs" data-testid="hist-vendor-select">
              <option value="">Seleccionar vendedor...</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.nombre} - {v.ficha_casmu}</option>)}
            </select>
            <div className="flex items-center gap-3">
              <button onClick={prevMonth} className="btn-ghost p-2" data-testid="hist-prev-month"><ChevronLeft size={18} /></button>
              <span className="text-[15px] font-semibold text-[#0A0A0A] capitalize min-w-[140px] text-center">{monthLabel}</span>
              <button onClick={nextMonth} className="btn-ghost p-2" data-testid="hist-next-month"><ChevronRight size={18} /></button>
            </div>
            {selectedVendor && (
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-historical">
                <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
              </button>
            )}
          </div>

          {selectedVendor ? (
            <div className="mac-card p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0082D2]/10 flex items-center justify-center">
                  <ShoppingCart size={20} className="text-[#0082D2]" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#0A0A0A]">{selectedVendor.nombre}</h3>
                  <p className="text-[11px] text-gray-500">Ficha: {selectedVendor.ficha_casmu} - {monthLabel}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PRODUCTS.map(product => (
                  <div key={product} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-[#0082D2]/30 transition-colors">
                    <span className="text-[13px] font-medium text-[#0A0A0A]">{product}</span>
                    <input type="number" min="0" step="1"
                      value={totales[product] || ''}
                      placeholder="0"
                      onChange={(e) => updateProduct(product, e.target.value)}
                      className="w-20 text-[13px] px-3 py-1.5 rounded-lg border border-gray-200 text-center font-semibold focus:border-[#0082D2] focus:outline-none focus:ring-1 focus:ring-[#0082D2]/30"
                      data-testid={`hist-${product.replace(/\s|\./g, '-').toLowerCase()}`} />
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-[13px] font-semibold text-gray-600">Total</span>
                <span className="text-xl font-bold text-[#0082D2]">{totalGeneral}</span>
              </div>
            </div>
          ) : (
            <div className="mac-card p-12 text-center">
              <ShoppingCart size={48} className="mx-auto text-gray-200 mb-4" />
              <p className="text-gray-500 text-[13px]">Selecciona un vendedor y mes para cargar sus ventas</p>
            </div>
          )}
        </>
      )}

      {tab === 'evolucion' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-[13px] font-medium text-gray-600">Ano:</label>
              <select value={evoYear} onChange={(e) => setEvoYear(Number(e.target.value))}
                className="form-input w-24" data-testid="evo-year-select">
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-500" />
              <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
                className="form-input w-40" data-testid="evo-product-filter">
                <option value="Total">Todos (Total)</option>
                {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <button onClick={exportEvolutionExcel} className="btn-ghost flex items-center gap-2 text-[12px]" data-testid="evo-export-excel">
              <Download size={14} /> Excel
            </button>
          </div>

          {loadingEvo ? (
            <div className="mac-card p-12 text-center">
              <p className="text-gray-500 text-[13px]">Cargando evolucion...</p>
            </div>
          ) : evolution.length === 0 ? (
            <div className="mac-card p-12 text-center">
              <TrendingUp size={48} className="mx-auto text-gray-200 mb-4" />
              <p className="text-gray-500 text-[13px]">No hay datos de ventas para {evoYear}</p>
            </div>
          ) : (
            <div className="mac-card overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="evo-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-3 font-semibold text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-[140px]">Vendedor</th>
                    {productFilter === 'Total' && PRODUCTS.map(p => (
                      <th key={p} className="text-center py-3 px-2 font-semibold text-gray-500 min-w-[50px] text-[10px]">{p}</th>
                    ))}
                    {MESES.map(m => (
                      <th key={m} className="text-center py-3 px-2 font-semibold text-gray-600 min-w-[50px]">{m}</th>
                    ))}
                    <th className="text-center py-3 px-3 font-bold text-[#00205B] min-w-[60px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {evolution.sort((a, b) => getVendorYearTotal(b) - getVendorYearTotal(a)).map(v => {
                    const yearTotal = getVendorYearTotal(v);
                    return (
                      <tr key={v.vendedor_id} className="border-b border-gray-100 hover:bg-[#0082D2]/5 transition-colors">
                        <td className="py-2.5 px-3 font-medium text-[#0A0A0A] sticky left-0 bg-white z-10">
                          <div>{v.nombre}</div>
                          <div className="text-[10px] text-gray-400">{v.ficha_casmu}</div>
                        </td>
                        {productFilter === 'Total' && PRODUCTS.map(p => {
                          const pTotal = Array.from({ length: 12 }, (_, i) => v.meses[String(i + 1)]?.totales?.[p] || 0).reduce((s, x) => s + x, 0);
                          return (
                            <td key={p} className="text-center py-2.5 px-2 text-[11px]">
                              {pTotal > 0 ? <span className="font-semibold text-gray-700">{pTotal}</span> : <span className="text-gray-300">-</span>}
                            </td>
                          );
                        })}
                        {Array.from({ length: 12 }, (_, i) => {
                          const val = getMonthValue(v, i + 1);
                          return (
                            <td key={i} className="text-center py-2.5 px-2">
                              {val > 0 ? <span className="font-semibold text-[#0A0A0A]">{val}</span> : <span className="text-gray-300">-</span>}
                            </td>
                          );
                        })}
                        <td className="text-center py-2.5 px-3 font-bold text-[#00205B]">{yearTotal > 0 ? yearTotal : '-'}</td>
                      </tr>
                    );
                  })}
                  {/* Total row */}
                  <tr className="border-t-2 border-[#00205B]/20 bg-[#00205B]/5 font-bold">
                    <td className="py-3 px-3 text-[#00205B] sticky left-0 bg-[#00205B]/5 z-10">TOTAL</td>
                    {productFilter === 'Total' && PRODUCTS.map(p => {
                      const colTotal = evolution.reduce((s, v) => s + Array.from({ length: 12 }, (_, i) => v.meses[String(i + 1)]?.totales?.[p] || 0).reduce((a, b) => a + b, 0), 0);
                      return (
                        <td key={p} className="text-center py-3 px-2 text-[#00205B] text-[11px]">
                          {colTotal > 0 ? colTotal : '-'}
                        </td>
                      );
                    })}
                    {Array.from({ length: 12 }, (_, i) => {
                      const monthTotal = evolution.reduce((s, v) => s + getMonthValue(v, i + 1), 0);
                      return (
                        <td key={i} className="text-center py-3 px-2 text-[#00205B]">
                          {monthTotal > 0 ? monthTotal : '-'}
                        </td>
                      );
                    })}
                    <td className="text-center py-3 px-3 text-[#00205B] text-[14px]">
                      {evolution.reduce((s, v) => s + getVendorYearTotal(v), 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
